import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tabs, TabContainer, Container } from '@gpstrackit/gtc-react-controls';
import { HasPermission } from 'Core/security';
import { endpoints } from 'Core/defaultValues';
import InspectionHistoryTab from './components/inspectionHistoryTab';
import DefectsTab from './components/defectsTab';
import MissingInspectionsTab from './components/missingInspectionsTab';
import InspectionBuilderTab from './components/inspectionBuilderTab';

/**
 * EVIR Tab userCodes for subuser permission checking.
 * These map to the EVIR submodule permissions in the Role Editor.
 * UserCodes are environment-specific (assigned by database).
 * Wrapped in arrays because HasPermission converts array items to string for comparison.
 */
const EVIR_USER_CODES_BY_ENV = {
  development: {
    INSPECTION_HISTORY: [370],  // evir:read:inspections - View Inspection History
    DEFECTS: [369],             // evir:read:defects - View Defects
    INSPECTION_BUILDER: [364],  // ect2:read:company - View Inspection Builder
  },
  staging: {
    INSPECTION_HISTORY: [370],  // evir:read:inspections - View Inspection History
    DEFECTS: [369],             // evir:read:defects - View Defects
    INSPECTION_BUILDER: [364],  // ect2:read:company - View Inspection Builder
  },
  beta: {
    INSPECTION_HISTORY: [370],  // evir:read:inspections - View Inspection History
    DEFECTS: [369],             // evir:read:defects - View Defects
    INSPECTION_BUILDER: [364],  // ect2:read:company - View Inspection Builder
  },
  production: {
    INSPECTION_HISTORY: [370],  // evir:read:inspections - View Inspection History
    DEFECTS: [369],             // evir:read:defects - View Defects
    INSPECTION_BUILDER: [364],  // ect2:read:company - View Inspection Builder
  },
};

// Get userCodes for current environment, fallback to staging if unknown
const getEvirUserCodes = () => {
  const env = endpoints.ENVIROMENT || 'staging';
  return EVIR_USER_CODES_BY_ENV[env] || EVIR_USER_CODES_BY_ENV.staging;
};

const EvirPxMainView = () => {
  const { messages } = useIntl();
  const { tabId } = useParams();
  const user = useSelector(state => state.securityRedux.user);
  const EVIR_USER_CODES = getEvirUserCodes();

  // For subusers, check 'evir' siteOption + specific userCode in user.options
  // For main users, checking 'evir' siteOption alone grants full access
  const tabsItems = [
    HasPermission(user, 'evir', EVIR_USER_CODES.INSPECTION_HISTORY) && {
      label: messages['evirPx_inspection_history'],
      component: <InspectionHistoryTab />
    },
    HasPermission(user, 'evir', EVIR_USER_CODES.DEFECTS) && {
      label: messages['evirPx_defects'],
      component: <DefectsTab />
    },
    HasPermission(user, 'viewbadreportlog') && {
      label: messages['evirPx_missing_inspections'],
      component: <MissingInspectionsTab />
    },
    HasPermission(user, 'evir', EVIR_USER_CODES.INSPECTION_BUILDER) && {
      label: messages['evirPx_inspection_builder'],
      component: <InspectionBuilderTab />
    },
  ].filter(Boolean).map((item, index) => ({ ...item, index }));

  if (tabsItems.length === 0) {
    return (
      <Container maxWidth={false} className="container-view">
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          {messages['evirPx_no_access'] || 'You do not have access to EVIR'}
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className="container-view">
      <Tabs tab={tabId?.length > 0 ? parseInt(tabId) : 0}>
        {tabsItems.map((item) => {
          const { label, component, index } = item;
          return (
            <TabContainer key={index} label={label} index={index}>
              {component}
            </TabContainer>
          );
        })}
      </Tabs>
    </Container>
  );
};

export default EvirPxMainView;
